<template>
  <main class="confirm-page">
    <section class="confirm-card">
      <div class="confirm-brand">
        <div class="confirm-brand__title">Подтверждение Email</div>
        <div class="confirm-brand__subtitle">Проверяем ссылку подтверждения аккаунта</div>
      </div>

      <div class="confirm-body">
        <div v-if="isLoading" class="confirm-loading">
          <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          <span>{{ loadingMessage }}</span>
        </div>

        <div v-else-if="isSuccess" class="confirm-success">
          <div class="confirm-success__icon"><i class="bi bi-check-circle-fill"></i></div>
          <div class="confirm-success__title">Email успешно подтверждён</div>
          <div class="confirm-success__text">Вы вошли в систему. Перенаправляем в дашборд…</div>
          <router-link class="btn btn-primary mt-3" to="/dashboard">Перейти сейчас</router-link>
        </div>

        <template v-else>
          <div class="alert alert-danger py-2 mb-3">
            {{ responseMessage }}
          </div>

          <div class="confirm-actions">
            <router-link class="btn btn-primary" to="/register">
              К регистрации
            </router-link>
            <router-link class="btn btn-outline-secondary" to="/">
              На главную
            </router-link>
          </div>
        </template>
      </div>
    </section>
  </main>
</template>

<script setup>
import axios from 'axios'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

const REDIRECT_DELAY_MS = 3000

const route = useRoute()
const router = useRouter()
const store = useStore()

const isLoading = ref(true)
const isSuccess = ref(false)
const responseMessage = ref('')
const loadingMessage = ref('Подтверждаем email...')

let redirectTimer = null

const token = computed(() => String(route.query.token || '').trim())

function clearRedirectTimer() {
  if (redirectTimer) {
    clearTimeout(redirectTimer)
    redirectTimer = null
  }
}

async function confirmEmail() {
  clearRedirectTimer()

  const currentToken = token.value
  if (!currentToken) {
    isLoading.value = false
    isSuccess.value = false
    responseMessage.value = 'Ссылка подтверждения некорректна: отсутствует token.'
    return
  }

  isLoading.value = true
  isSuccess.value = false
  loadingMessage.value = 'Подтверждаем email...'
  responseMessage.value = ''
  try {
    const { data } = await axios.get('/api/account/confirm', {
      params: { token: currentToken },
    })

    // On success the backend returns a JWT — log the user in, then redirect after a short pause.
    const jwt = data?.success ? String(data?.data || '') : ''
    if (jwt) {
      localStorage.setItem('jwt', jwt)
      axios.defaults.headers.common.Authorization = `Bearer ${jwt}`
      await store.dispatch('fetchCurrentUser')
      isSuccess.value = true
      isLoading.value = false
      redirectTimer = setTimeout(() => {
        router.replace('/dashboard')
      }, REDIRECT_DELAY_MS)
      return
    }

    responseMessage.value = String(data?.data || 'Не удалось подтвердить email.')
  } catch (error) {
    const payload = error?.response?.data
    responseMessage.value = String(
      payload?.data || payload?.message || 'Неверный или истёк срок действия токена.'
    )
  } finally {
    if (!isSuccess.value) isLoading.value = false
  }
}

onMounted(confirmEmail)
watch(() => route.query.token, confirmEmail)
onBeforeUnmount(clearRedirectTimer)
</script>

<style scoped>
.confirm-page {
  min-height: calc(100vh - 102px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background:
    radial-gradient(circle at 15% 18%, rgba(16, 185, 129, 0.1), transparent 40%),
    radial-gradient(circle at 86% 82%, rgba(13, 110, 253, 0.1), transparent 45%),
    #f7f9fd;
}

.confirm-card {
  width: 100%;
  max-width: 520px;
  border: 1px solid #e8edf7;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 16px 40px rgba(17, 24, 39, 0.12);
}

.confirm-brand {
  padding: 18px 20px 14px;
  color: #fff;
  background: linear-gradient(135deg, #0f766e, #0ea5e9);
}

.confirm-brand__title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}

.confirm-brand__subtitle {
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.92;
}

.confirm-body {
  padding: 18px 20px 20px;
}

.confirm-loading {
  display: inline-flex;
  align-items: center;
  color: #4b5a78;
  font-size: 14px;
}

.confirm-success {
  text-align: center;
  padding: 6px 0 2px;
}

.confirm-success__icon {
  font-size: 46px;
  line-height: 1;
  color: #16a34a;
}

.confirm-success__title {
  margin-top: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #14532d;
}

.confirm-success__text {
  margin-top: 4px;
  font-size: 14px;
  color: #4b5a78;
}

.confirm-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.confirm-actions .btn {
  min-width: 160px;
}

@media (max-width: 576px) {
  .confirm-page {
    padding: 14px;
  }

  .confirm-actions .btn {
    min-width: 0;
    width: 100%;
  }
}
</style>
